<script setup>
import { defineProps, reactive } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { Link } from '@inertiajs/inertia-vue3';
import ButtonShape from "@/Shared/ButtonShape";
import Pagination from '@/Models/Pagination';
import DeleteModal from "@/Shared/Dashboard/Modals/DeleteModal";
import { isEmpty } from 'lodash';
import PositionCategory from "@/Models/PositionCategory";

let props = defineProps({
    positionCategories: Object,
    current_url: String,
});

let deleteCategorySettings = reactive({
    deleteCategoryModalIsOpen: false,
    categoryToBeDelete: null,
})

let pagination = reactive(new Pagination(props.positionCategories));

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);
function UTCToHumanReadable(u) {
    return dayjs(u).utc().local().tz(dayjs.tz.guess()).format('MMMM DD, YYYY hh:mm A');
}

function deleteCategory() {
    if (isEmpty(deleteCategorySettings.categoryToBeDelete)) {
        return;
    }
    let categoryObject = new PositionCategory(deleteCategorySettings.categoryToBeDelete);
    categoryObject.delete()
    deleteCategorySettings.deleteCategoryModalIsOpen = false
}
</script>

<template>
    <DeleteModal :open="deleteCategorySettings.deleteCategoryModalIsOpen" modalTitle="Delete Category" :modalDescription="(deleteCategorySettings.categoryToBeDelete)?'Are you sure you want to Delete ('+deleteCategorySettings.categoryToBeDelete.name+') Category? All of your data will be permanently removed from our servers forever. This action cannot be undone.':''">
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button type="button" class="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm" @click.prevent="deleteCategory()">Delete</button>
            <button type="button" class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm" @click.prevent="deleteCategorySettings.deleteCategoryModalIsOpen = false">Cancel</button>
        </div>
    </DeleteModal>
    <div class="max-w-7xl mx-auto px-4 flex justify-between items-center">
        <h1 class="text-2xl font-semibold text-gray-900">Position Categories</h1>
        <Link href="/backoffice/position-categories/create" class="text-sm text-wgn-blue-4">Add new Category</Link>
    </div>
    <div class="max-w-7xl mx-auto px-4">
        <div class="py-4">
            <div>
                <div class="mb-2 overflow-hidden">
                    <div class="rounded-lg bg-white px-4 sm:px-0 lg:px-0">
                        <div class="flex flex-col border border-gray-200 md:rounded-lg">
                            <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                        <table class="min-w-full divide-y divide-gray-300">
                                            <thead class="bg-gray-50">
                                            <tr>
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                >
                                                    <span class="group inline-flex">ID</span>
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                >
                                                    <span class="group inline-flex">Name</span>
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                >
                                                    <span class="group inline-flex">Created At</span>
                                                </th>
                                                <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                    <span class="sr-only">Edit</span>
                                                    <span class="sr-only">Delete</span>
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody class="divide-y divide-gray-200 bg-white">
                                            <tr v-for="category in positionCategories.data" :key="category.id">
                                                <td
                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                                                >
                                                    {{ category.id }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ category.name }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ UTCToHumanReadable(category.created_at) }}
                                                </td>
                                                <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <Link :href="route('position-categories.edit', category.id)" class="text-indigo-600 hover:text-indigo-900 mr-3">Edit</Link>
                                                    <button @click.prevent="deleteCategorySettings.deleteCategoryModalIsOpen = true; deleteCategorySettings.categoryToBeDelete = category" class="text-red-600 hover:text-indigo-900">Delete</button>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <nav
                        class="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white bg-white px-4 py-3 sm:px-6"
                        aria-label="Pagination"
                    >
                        <div class="hidden sm:block">
                            <p class="font-inter text-sm text-gray-700">
                                Showing
                                {{ ' ' }}
                                <span class="font-medium">{{ pagination.meta.from }}</span>
                                {{ ' ' }}
                                to
                                {{ ' ' }}
                                <span class="font-medium">{{ pagination.meta.to }}</span>
                                {{ ' ' }}
                                of
                                {{ ' ' }}
                                <span class="font-medium">{{ pagination.meta.total }}</span>
                                {{ ' ' }}
                                results
                            </p>
                        </div>
                        <div class="flex flex-1 justify-between space-x-4 sm:justify-end">
                            <Link :href="pagination.links.prev">
                                <ButtonShape v-if="pagination.links.prev" type="gray"> Previous</ButtonShape>
                            </Link>
                            <Link :href="pagination.links.next">
                                <ButtonShape v-if="pagination.links.next" type="gray"> Next</ButtonShape>
                            </Link>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BackofficeLayout from '@/Layouts/BackofficeLayout'
export default {
    layout: BackofficeLayout,
}
</script>
